import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Shield,
  ArrowLeft,
  Globe,
  Search,
  Palette,
  Euro,
  FileText,
  Image,
  Users,
  Megaphone,
  Settings,
  MessageCircle,
  Monitor,
  FileSignature,
  Link2,
  Star,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';

// =============================================
// ADMIN LAYOUT - Avec Sidebar dédiée
// =============================================

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    site: true,
    marketing: true,
    config: true,
    integrations: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminNavigation = [
    {
      section: 'site',
      label: 'Site Vitrine',
      icon: Globe,
      color: '#3b82f6',
      items: [
        { to: '/page-builder', icon: Globe, label: 'Pages du site' },
        { to: '/team-portfolio', icon: Users, label: 'Équipe & Portfolio' },
        { to: '/website/gallery', icon: Image, label: 'Galerie clients' },
        { to: '/website/case-studies', icon: Image, label: 'Études de cas' },
      ]
    },
    {
      section: 'marketing',
      label: 'Marketing & SEO',
      icon: Search,
      color: '#10b981',
      items: [
        { to: '/settings/news', icon: Megaphone, label: 'Actualités' },
        { to: '/knowledge', icon: Star, label: 'Base de connaissances' },
        { to: '/admin/promo-banner', icon: Megaphone, label: 'Bandeau promo' },
      ]
    },
    {
      section: 'config',
      label: 'Configuration',
      icon: Settings,
      color: '#f59e0b',
      items: [
        { to: '/settings/pricing', icon: Euro, label: 'Tarification & Packs' },
        { to: '/settings/theme', icon: Palette, label: 'Thème & Couleurs' },
        { to: '/settings/agency-info', icon: Settings, label: 'Informations agence' },
        { to: '/settings/promo-banner', icon: Monitor, label: 'Bandeau site' },
      ]
    },
    {
      section: 'integrations',
      label: 'Intégrations',
      icon: Link2,
      color: '#8b5cf6',
      items: [
        { to: '/settings/smtp', icon: MessageCircle, label: 'Email (SMTP)' },
        { to: '/settings/integrations/axonaut', icon: Link2, label: 'Axonaut' },
        { to: '/settings/templates', icon: FileText, label: 'Modèles documents' },
      ]
    },
  ];

  const renderNavItem = (item, sectionColor) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm",
            isActive
              ? "bg-amber-500/20 text-amber-200 font-medium"
              : "text-white/60 hover:text-white hover:bg-white/5"
          )
        }
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  const renderSection = (section) => {
    const Icon = section.icon;
    const isExpanded = expandedSections[section.section];
    
    return (
      <div key={section.section} className="mb-2">
        <button
          onClick={() => toggleSection(section.section)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all"
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" style={{ color: section.color }} />
            <span className="text-sm font-medium">{section.label}</span>
          </div>
          <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded && "rotate-180")} />
        </button>
        
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
            {section.items.map(item => renderNavItem(item, section.color))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Sidebar ADMIN */}
      <aside className="fixed top-0 left-0 h-full w-64 z-40 flex flex-col bg-gradient-to-b from-amber-900 to-amber-950">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">ADMIN</h1>
              <p className="text-white/50 text-xs">Gestion du site</p>
            </div>
          </div>
        </div>

        {/* Back to V1 */}
        <div className="p-3 border-b border-white/10">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard/agency')}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour Espace Client
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {adminNavigation.map(section => renderSection(section))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.first_name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.first_name}</p>
              <p className="text-white/50 text-xs truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/50 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-64">
        {/* Simple Header */}
        <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between sticky top-0 z-30">
          <h2 className="font-semibold text-[#1a365d]">Administration</h2>
          <span className="text-sm text-slate-500">{user?.first_name}</span>
        </header>
        
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
